<template>
    <div>
        <b-modal
            id="m-bulk-approve-temuan"
            title="Approve Sekaligus ke Inventory"
            size="xl"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            @show="onShow"
            @hide="onHide"
        >
            <p class="text-muted">
                Daftar temuan yang sudah disetujui audit (tidak termasuk titip jual dan grade X).
            </p>
            <b-alert :show="allIds.length > MAX_IDS" variant="info">
                Ada {{ allIds.length }} temuan, sedangkan maksimal {{ MAX_IDS }} temuan per sekali approve.
                "Pilih Semua" hanya memilih {{ MAX_IDS }} temuan terlama. Sisanya bisa di-approve setelah ini selesai.
            </b-alert>
            <div class="d-flex align-items-center mb-2">
                <b-button size="sm" variant="primary" class="mr-2" :disabled="isBusy || isSubmitting" @click="selectAll">
                    Pilih Semua (maks. {{ MAX_IDS }})
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" :disabled="isSubmitting" @click="selected = []">
                    Batal Pilih
                </b-button>
                <strong>{{ selected.length }} dipilih</strong>
            </div>

            <b-table bordered hover show-empty small responsive :busy="isBusy" :items="items" :fields="fields">
                <template v-slot:empty>
                    Tidak ada temuan yang dapat di-approve sekaligus.
                </template>
                <template v-slot:cell(select)="row">
                    <b-form-checkbox
                        v-model="selected"
                        :value="row.item.id"
                        :disabled="!selected.includes(row.item.id) && selected.length >= MAX_IDS"
                    />
                </template>
                <template v-slot:cell(item_type)="row">
                    {{ row.item.item_type }} <span v-if="row.item.year != null">({{ row.item.year }})</span>
                </template>
                <template v-slot:cell(is_batangan)="row">
                    {{ row.item.is_batangan ? "Batangan" : "Lengkap" }}
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="getItems"
            />

            <div v-if="result" class="mt-3">
                <b-alert show :variant="result.failed.length ? 'warning' : 'success'">
                    Berhasil: {{ result.success.length }}, Gagal: {{ result.failed.length }}
                </b-alert>
                <b-alert :show="!!result.printError" variant="danger">{{ result.printError }}</b-alert>
                <ul v-if="result.failed.length" class="text-danger">
                    <li v-for="f in result.failed" :key="f.id">{{ f.no_faktur }}: {{ f.message }}</li>
                </ul>
                <b-button v-if="result.link" variant="dark" @click="$bvModal.show('m-bulk-approve-barcode')">
                    <i class="fa fa-fw fa-print mr-1" />Cetak Barcode
                </b-button>
            </div>

            <div class="text-right mt-3">
                <b-button variant="secondary" class="mr-2" :disabled="isSubmitting" @click="$bvModal.hide('m-bulk-approve-temuan')">
                    Tutup
                </b-button>
                <b-button variant="success" :disabled="isSubmitting || !selected.length" @click="submit">
                    {{ isSubmitting ? "Memproses..." : "Approve ke Inventory" }}
                </b-button>
            </div>
        </b-modal>

        <b-modal
            id="m-bulk-approve-barcode"
            title="Cetak Barcode"
            centered
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            hide-header-close
        >
            Silahkan cetak barcode dibawah ini. Mohon pastikan barcode tercetak dengan baik sebelum melanjutkan.
            <br /><br />
            <iframe :src="barcodeSrc" height="300px" width="100%" />
            <div class="mt-4 d-flex justify-content-between">
                <b-button v-if="result && result.linkDamage" variant="dark" @click="showDamage = !showDamage">
                    Print {{ showDamage ? "Barcode" : "Kerusakan" }}
                </b-button>
                <b-button variant="primary" class="ml-auto" @click="$bvModal.hide('m-bulk-approve-barcode')">Tutup</b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
const MAX_IDS = 30;

export default {
    name: "ModalBulkApproveTemuan",
    data() {
        return {
            MAX_IDS,
            isBusy: false,
            isSubmitting: false,
            items: [],
            allIds: [],
            // Kept as an array of ids (not per-page) so the selection survives paging.
            selected: [],
            result: null,
            showDamage: false,
            fields: [
                { key: "select", label: "" },
                { key: "no_faktur", label: "NF" },
                { key: "imei_sn", label: "IMEI/SN" },
                { key: "item_kind", label: "Jenis" },
                { key: "item_brand", label: "Merk" },
                { key: "item_type", label: "Tipe" },
                { key: "grade", label: "Grade" },
                { key: "spec", label: "Spek" },
                { key: "is_batangan", label: "Kelengkapan" }
            ],
            options: [10, 20, 30, 40, 50].map(value => ({ value, text: String(value) })),
            currentPage: 1,
            perPage: 10,
            totalRows: 0
        };
    },
    computed: {
        barcodeSrc() {
            if (!this.result) return "";
            return this.$BASE_PRINT_FILE_PATH + (this.showDamage ? this.result.linkDamage : this.result.link);
        }
    },
    methods: {
        onShow() {
            this.selected = [];
            this.result = null;
            this.showDamage = false;
            this.getItems(1);
        },

        getItems(page = 1) {
            this.isBusy = true;
            this.currentPage = page;
            return this.$axios
                .get("inventory-variances/bulk-complete" + this.$helper.getBasePagedQuery(page, this.perPage))
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                    this.allIds = res.data.data.all_ids;
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isBusy = false));
        },

        // The header close stays usable, so block it until the result is back.
        onHide(event) {
            if (this.isSubmitting) event.preventDefault();
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(1);
        },

        selectAll() {
            this.selected = this.allIds.slice(0, MAX_IDS);
        },

        async submit() {
            if (!confirm(`Approve ${this.selected.length} temuan ke inventory?`)) return;

            this.isSubmitting = true;
            try {
                const res = await this.$axios.post("inventory-variances/bulk-complete", { ids: this.selected });
                this.result = res.data.data;
                this.showDamage = false;
                this.selected = [];
                this.$helper.toastSuccess(this, `${this.result.success.length} temuan berhasil di-approve`);
                if (this.result.link) this.$bvModal.show("m-bulk-approve-barcode");
                this.$emit("saved");
                await this.getItems(1);
            } catch (error) {
                this.$helper.parseError(this, error);
            } finally {
                this.isSubmitting = false;
            }
        }
    }
};
</script>
